/** Combined Created/Updated timestamps cell — shared by the Bookings and Orders
 *  grids.
 *
 * Renders two lines:
 *    Created [<source>]        <- source label optional (Orders have none)
 *    <local timestamp>         <- hover/click opens a CellActionsPopover
 *    Updated [<source>]
 *    <local timestamp>
 *
 * The timestamp value shows local time; the popover title carries the
 * unambiguous UTC instant, plus ±delta range-filter presets that REPLACE the
 * matching `created`/`updated` condition on the owning grid's query (wired via
 * `onReplaceFilter`). Omit `onReplaceFilter` to render the value without filter
 * actions.
 *
 * The bracketed `[<source>]` gets its own popover when `onSourceFilter` is
 * passed (Bookings), filtering the grid by that create/update source. Omit it
 * and the bracket stays a plain label.
 *
 * `TimestampValueCell` is the single-timestamp half of this, exported for grids
 * that also expose raw sortable Created/Updated columns and want the same
 * ±delta popover on them — or the same popover on any other instant namespace
 * (the bookings grid's zoned Trip start / Trip end, `start` / `end`).
 */
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { formatDate } from '@/utils/format';
import { TIME_DELTAS } from '@/utils/gridLinks';

import { CellActionsPopover, type CellAction } from './CellActionsPopover';

export type TimestampNamespace = 'created' | 'updated';

/** Replace (or, with null, remove) a `created`/`updated` range condition on the
 *  owning grid's query. Deliberately narrow — the only cond a timestamp cell
 *  ever produces is a range between two ISO instants. A wider page-level
 *  `onReplaceFilter` (e.g. bookings') is assignable to this. */
export interface TimestampsReplaceFn {
  (ns: TimestampNamespace, cond: { operator: 'range'; value: [string, string] } | null): void;
}

/** The create/update PROVENANCE namespaces (Group_Tour_User.SOURCES), filtered
 *  by their human label. `null` resets the condition. Single-valued per row, so
 *  the owning grid is expected to REPLACE rather than stack. */
export interface TimestampsSourceFilterFn {
  (ns: 'create_source' | 'update_source', value: string | null): void;
}

/** Second-precision ISO instant — millis would just be noise in the query box. */
const isoSecond = (ms: number) => `${new Date(ms).toISOString().slice(0, 19)}Z`;

/** The grid renders local time; the popover title shows the unambiguous UTC. */
const utcTs = (iso: string) => `${new Date(iso).toISOString().slice(0, 19).replace('T', ' ')} UTC`;

/** Replace (or remove) a range condition on any instant namespace `N`. */
type InstantReplaceFn<N extends string> = (
  ns: N,
  cond: { operator: 'range'; value: [string, string] } | null,
) => void;

/** The ±delta presets + reset for one timestamp namespace. `[]` when the owning
 *  grid passed no `onReplaceFilter` (renders the value as plain text). */
function deltaActions<N extends string>(
  ns: N,
  iso: string,
  onReplaceFilter?: InstantReplaceFn<N> | undefined,
): CellAction[] {
  if (!onReplaceFilter) return [];
  const ts = new Date(iso).getTime();
  return [
    // The presets are shared with the detail pages' value popovers (gridLinks).
    ...TIME_DELTAS.map(({ label: dLabel, tooltip, ms }) => ({
      key: dLabel,
      label: dLabel,
      tooltip: `Filter ${ns} to ${tooltip} around this timestamp`,
      onClick: () =>
        onReplaceFilter(ns, {
          operator: 'range' as const,
          value: [isoSecond(ts - ms), isoSecond(ts + ms)],
        }),
    })),
    {
      key: 'reset',
      label: `Reset ${ns} filter`,
      icon: <FilterAltOffIcon fontSize="small" />,
      onClick: () => onReplaceFilter(ns, null),
    },
  ];
}

/** A bare timestamp value (local time) carrying the same UTC title + ±delta
 *  popover as the combined column — for grids that ALSO expose raw sortable
 *  Created/Updated columns. `children` replaces the local-time text (e.g. the
 *  instant in the record's own zone). Renders '—' for a null instant. */
export function TimestampValueCell<N extends string = TimestampNamespace>({
  ns,
  iso,
  onReplaceFilter,
  children,
}: {
  ns: N;
  iso: string | null;
  onReplaceFilter?: InstantReplaceFn<N> | undefined;
  children?: ReactNode;
}) {
  if (!iso) return <>—</>;
  return (
    <CellActionsPopover auto title={utcTs(iso)} actions={deltaActions(ns, iso, onReplaceFilter)}>
      {children ?? formatDate(iso)}
    </CellActionsPopover>
  );
}

/** One two-line block: `Created [<source>]` then the timestamp. Hovering (or
 *  clicking) the timestamp opens a CellActionsPopover with the UTC instant and
 *  ±delta range-filter presets against the matching created/updated namespace;
 *  the `[source]` bracket gets its own filter/reset popover when the grid wired
 *  `onSourceFilter`. */
function TimestampLine({
  label,
  ns,
  sourceNs,
  iso,
  source,
  onReplaceFilter,
  onSourceFilter,
}: {
  label: string;
  ns: TimestampNamespace;
  /** The provenance namespace paired with this timestamp. */
  sourceNs: 'create_source' | 'update_source';
  iso: string | null;
  /** Omit entirely (undefined) to hide the `[source]` tag — Orders have no
   *  create/update source. An empty string still renders `[]` (Bookings). */
  source?: string | undefined;
  onReplaceFilter?: TimestampsReplaceFn | undefined;
  onSourceFilter?: TimestampsSourceFilterFn | undefined;
}) {
  if (!iso) return null;
  const actions = deltaActions(ns, iso, onReplaceFilter);
  // An empty source label is a real value server-side only in the sense of
  // "unknown" — there is nothing useful to filter by, so keep it inert.
  const sourceActions: CellAction[] =
    onSourceFilter && source
      ? [
          {
            key: 'filter',
            label: `Filter by this source (${sourceNs}=${source})`,
            icon: <FilterAltIcon fontSize="small" />,
            onClick: () => onSourceFilter(sourceNs, source),
          },
          {
            key: 'reset',
            label: `Reset ${sourceNs} filter`,
            icon: <FilterAltOffIcon fontSize="small" />,
            onClick: () => onSourceFilter(sourceNs, null),
          },
        ]
      : [];
  return (
    <Typography component="div" variant="caption" sx={{ lineHeight: 1.5, whiteSpace: 'nowrap' }}>
      <Box component="div" sx={{ color: 'text.secondary' }}>
        {label}
        {source !== undefined && (
          <>
            {' '}
            <Typography component="span" variant="caption" color="text.disabled">
              {sourceActions.length > 0 ? (
                <CellActionsPopover auto actions={sourceActions} sx={{ display: 'inline' }}>
                  [{source}]
                </CellActionsPopover>
              ) : (
                <>[{source}]</>
              )}
            </Typography>
          </>
        )}
      </Box>
      <Box component="div">
        <CellActionsPopover auto title={utcTs(iso)} actions={actions}>
          {formatDate(iso)}
        </CellActionsPopover>
      </Box>
    </Typography>
  );
}

export interface TimestampsCellProps {
  created: string | null;
  updated: string | null;
  /** Source labels (Bookings only). Omit both for Orders. */
  createdSource?: string | undefined;
  updatedSource?: string | undefined;
  onReplaceFilter?: TimestampsReplaceFn | undefined;
  /** Makes the `[source]` brackets filterable. Omit to keep them plain labels. */
  onSourceFilter?: TimestampsSourceFilterFn | undefined;
}

export function TimestampsCell({
  created,
  updated,
  createdSource,
  updatedSource,
  onReplaceFilter,
  onSourceFilter,
}: TimestampsCellProps) {
  return (
    <Box sx={{ py: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <TimestampLine
        label="Created"
        ns="created"
        sourceNs="create_source"
        iso={created}
        source={createdSource}
        onReplaceFilter={onReplaceFilter}
        onSourceFilter={onSourceFilter}
      />
      <TimestampLine
        label="Updated"
        ns="updated"
        sourceNs="update_source"
        iso={updated}
        source={updatedSource}
        onReplaceFilter={onReplaceFilter}
        onSourceFilter={onSourceFilter}
      />
    </Box>
  );
}
